import React, { useState, useEffect, useRef } from 'react';
import apiService from '../../services/api';
import { uploadImage } from '../../services/upload';

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points: 0,
    thumbnail: '',
    start_time: '',
    end_time: ''
  });
  const [editingActivity, setEditingActivity] = useState(null);
  const [formError, setFormError] = useState('');
  const fileInputRef = useRef();

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await apiService.getActivities();
      setActivities(response.data);
    } catch (err) {
      setError('获取活动列表失败');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const formatDateTimeLocal = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    // 转成本地时间字符串 yyyy-MM-ddTHH:mm
    const pad = n => n.toString().padStart(2, '0');
    const yyyy = date.getFullYear();
    const MM = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());
    const hh = pad(date.getHours());
    const mm = pad(date.getMinutes());
    return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
  };

  // 计算base64图片体积（字节）
  function getBase64Size(base64String) {
    if (!base64String || typeof base64String !== 'string') return 0;
    const idx = base64String.indexOf('base64,');
    if (idx === -1) return 0;
    const base64 = base64String.slice(idx + 7);
    return Math.floor(base64.length * 3 / 4);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    try {
      const token = localStorage.getItem('token');
      
      if (editingActivity) {
        // 更新活动
        await apiService.updateActivity(editingActivity.id, formData);
      } else {
        // 创建新活动
        await apiService.createActivity(formData);
      }
      
      // 重置表单
      setFormData({
        name: '',
        description: '',
        points: 0,
        thumbnail: '',
        start_time: '',
        end_time: ''
      });
      setEditingActivity(null);
      
      // 重新获取活动列表
      fetchActivities();
    } catch (err) {
      setFormError(err.response?.data?.message || '操作失败');
      console.error(err);
    }
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity);
    setFormData({
      name: activity.name,
      description: activity.description || '',
      points: activity.points,
      thumbnail: activity.thumbnail || '',
      start_time: formatDateTimeLocal(activity.start_time),
      end_time: formatDateTimeLocal(activity.end_time)
    });
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm('确定要删除此活动吗？')) {
      return;
    }
    
    try {
      await apiService.deleteActivity(activityId);
      
      // 重新获取活动列表
      fetchActivities();
    } catch (err) {
      setError(err.response?.data?.message || '删除失败');
      console.error(err);
    }
  };

  const cancelEdit = () => {
    setEditingActivity(null);
    setFormData({
      name: '',
      description: '',
      points: 0,
      thumbnail: '',
      start_time: '',
      end_time: ''
    });
  };

  const isActiveActivity = (activity) => {
    const now = new Date();
    const startTime = new Date(activity.start_time);
    const endTime = new Date(activity.end_time);
    return startTime <= now && now <= endTime;
  };

  // 切换报名开放状态
  const handleToggleEnroll = async (activity) => {
    try {
      if (new Date(activity.end_time) < new Date()) {
        alert('已结束的活动无法更改报名状态');
        return;
      }
      if (activity.enroll_open) {
        await apiService.closeActivityEnroll(activity.id);
      } else {
        await apiService.openActivityEnroll(activity.id);
      }
      fetchActivities();
    } catch (err) {
      alert(err.response?.data?.message || '操作失败');
    }
  };

  // 搜索过滤
  const filteredActivities = activities.filter(a => {
    const kw = search.trim().toLowerCase();
    if (!kw) return true;
    return (
      (a.name && a.name.toLowerCase().includes(kw)) ||
      (a.description && a.description.toLowerCase().includes(kw)) ||
      (a.points && String(a.points).includes(kw))
    );
  });

  if (loading && activities.length === 0) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">加载中...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-4">活动管理</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="row">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
              <h4 className="mb-0">活动列表</h4>
              <input
                type="text"
                className="form-control form-control-sm ms-2"
                style={{ maxWidth: 220 }}
                placeholder="关键字搜索..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>活动名称</th>
                      <th>积分</th>
                      <th>状态</th>
                      <th>时间</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredActivities.map(activity => (
                      <tr key={activity.id}>
                        <td>{activity.id}</td>
                        <td>{activity.name}</td>
                        <td>{activity.points}</td>
                        <td>
                          {isActiveActivity(activity) ? (
                            <span className="badge bg-success">进行中</span>
                          ) : new Date(activity.start_time) > new Date() ? (
                            <span className="badge bg-warning">未开始</span>
                          ) : (
                            <span className="badge bg-secondary">已结束</span>
                          )}
                        </td>
                        <td>
                          <small>开始: {new Date(activity.start_time).toLocaleString()}</small><br />
                          <small>结束: {new Date(activity.end_time).toLocaleString()}</small>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => handleEdit(activity)}
                            >
                              编辑
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDelete(activity.id)}
                            >
                              删除
                            </button>
                            <button
                              className={`btn btn-sm ${activity.enroll_open ? 'btn-warning' : 'btn-success'}`}
                              disabled={new Date(activity.end_time) < new Date()}
                              onClick={() => handleToggleEnroll(activity)}
                            >
                              {activity.enroll_open ? '关闭报名' : '开启报名'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">{editingActivity ? '编辑活动' : '添加活动'}</h4>
            </div>
            <div className="card-body">
              {formError && <div className="alert alert-danger">{formError}</div>}
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">活动名称</label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="description" className="form-label">活动描述</label>
                  <textarea
                    className="form-control"
                    id="description"
                    name="description"
                    rows="3"
                    value={formData.description}
                    onChange={handleInputChange}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="points" className="form-label">积分</label>
                  <input
                    type="number"
                    className="form-control"
                    id="points"
                    name="points"
                    value={formData.points}
                    onChange={handleInputChange}
                    required
                    min="0"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label mb-2">缩略图</label>
                  <div className="mb-2">
                    <div className="d-flex flex-wrap gap-3">
                      {[
                        {src: '/images/activities/union.jpg', label: '工会活动'},
                        {src: '/images/activities/badminton.jpg', label: '羽毛球活动'},
                        {src: '/images/activities/pingpong.jpg', label: '乒乓球活动'},
                        {src: '/images/activities/outdoor.jpg', label: '户外活动'},
                        {src: '/images/activities/playground.jpg', label: '游乐场'},
                        {src: '/images/activities/party.jpg', label: '轰趴聚会'}
                      ].map(img => (
                        <div
                          key={img.src}
                          onClick={() => setFormData({...formData, thumbnail: img.src})}
                          className={`card p-1 text-center shadow-sm selectable-thumbnail ${formData.thumbnail===img.src ? 'border-primary border-3' : 'border-0'}`}
                          style={{width: 110, cursor: 'pointer', boxShadow: formData.thumbnail===img.src ? '0 0 0 2px #0d6efd' : undefined}}
                        >
                          <img src={img.src} alt={img.label} className="img-fluid rounded mb-1" style={{height: 60, objectFit: 'cover'}} />
                          <div style={{fontSize: 13}}>{img.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="form-text mt-1">点击选择预设图片</div>
                  </div>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-6">
                      <label htmlFor="thumbnail-upload" className="form-label">上传图片</label>
                      <input
                        id="thumbnail-upload"
                        type="file"
                        accept="image/*"
                        className="form-control"
                        ref={fileInputRef}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 524288) {
                              alert('上传图片不能超过512KB，请压缩后再上传。');
                              if (fileInputRef.current) fileInputRef.current.value = null;
                              return;
                            }
                            try {
                              const url = await uploadImage(file);
                              setFormData({...formData, thumbnail: url});
                            } catch (err) {
                              alert('图片上传失败，请重试');
                              if (fileInputRef.current) fileInputRef.current.value = null;
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="thumbnail-url" className="form-label">图片URL</label>
                      <input
                        type="text"
                        className="form-control"
                        id="thumbnail-url"
                        name="thumbnail"
                        value={formData.thumbnail}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">开始时间</label>
                  <div className="d-flex gap-2">
                    <input
                      type="date"
                      className="form-control"
                      value={formData.start_time ? formData.start_time.split('T')[0] : ''}
                      onChange={e => {
                        const date = e.target.value;
                        const time = formData.start_time ? formData.start_time.split('T')[1] : '00:00';
                        setFormData({...formData, start_time: date ? `${date}T${time}` : ''});
                      }}
                      required
                      style={{maxWidth: 160}}
                    />
                    <select
                      className="form-select"
                      value={formData.start_time ? formData.start_time.split('T')[1]?.slice(0,5) : '00:00'}
                      onChange={e => {
                        const time = e.target.value;
                        const date = formData.start_time ? formData.start_time.split('T')[0] : '';
                        setFormData({...formData, start_time: date ? `${date}T${time}` : ''});
                      }}
                      required
                      style={{maxWidth: 120}}
                    >
                      {Array.from({length: 48}, (_, i) => {
                        const hh = String(Math.floor(i/2)).padStart(2, '0');
                        const mm = i%2 === 0 ? '00' : '30';
                        const t = `${hh}:${mm}`;
                        return <option key={t} value={t}>{t}</option>;
                      })}
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">结束时间</label>
                  <div className="d-flex gap-2">
                    <input
                      type="date"
                      className="form-control"
                      value={formData.end_time ? formData.end_time.split('T')[0] : ''}
                      onChange={e => {
                        const date = e.target.value;
                        const time = formData.end_time ? formData.end_time.split('T')[1] : '00:00';
                        setFormData({...formData, end_time: date ? `${date}T${time}` : ''});
                      }}
                      required
                      style={{maxWidth: 160}}
                    />
                    <select
                      className="form-select"
                      value={formData.end_time ? formData.end_time.split('T')[1]?.slice(0,5) : '00:00'}
                      onChange={e => {
                        const time = e.target.value;
                        const date = formData.end_time ? formData.end_time.split('T')[0] : '';
                        setFormData({...formData, end_time: date ? `${date}T${time}` : ''});
                      }}
                      required
                      style={{maxWidth: 120}}
                    >
                      {Array.from({length: 48}, (_, i) => {
                        const hh = String(Math.floor(i/2)).padStart(2, '0');
                        const mm = i%2 === 0 ? '00' : '30';
                        const t = `${hh}:${mm}`;
                        return <option key={t} value={t}>{t}</option>;
                      })}
                    </select>
                  </div>
                </div>
                <div className="d-grid gap-2">
                  <button type="submit" className="btn btn-primary">
                    {editingActivity ? '更新活动' : '添加活动'}
                  </button>
                  {editingActivity && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={cancelEdit}
                    >
                      取消编辑
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Activities;
